import { FinancialProductStrategy } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { HoldingReport } from '@interfaces/holdingReport';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { SubAccountReport } from '@interfaces/subAccountReport';
import {
  allocationCellKey,
  parseAllocationCellKey,
  subAccountReportId,
} from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import {
  CUSTOM_OVERRIDE_TOLERANCE,
  MODEL_SNAP_TOLERANCE,
} from '@ovComponents/4-module/widgets/portfolio/components/allocationTolerances';
import {
  buildHouseholdRowTargetCents,
  buildMemberColumnTargetCents,
} from '@ovComponents/4-module/widgets/portfolio/components/lockedMatrixRedistribute';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import {
  canonicalizeWireMatrixFractions,
  fractionFromAmountCents,
  fractionToAmountCents,
  roundToWireDecimals,
} from '@utils/allocationUnits';

/** Narrowed so placeholders (e.g. portfolio.tsx) need no casts for unrelated PortfolioReport fields. */
export type AllocationPortfolioReport = Pick<
  PortfolioReport,
  'id' | 'subAccounts' | 'holdings' | 'expectedCashPercentage'
> &
  Partial<Pick<PortfolioReport, 'modelPortfolio'>>;

export type CustomAllocationHoldingReport = Pick<
  HoldingReport,
  | 'expectedPercentage'
  | 'customExpectedPercentage'
  | 'assetClassGroupPercentage'
> &
  Partial<Pick<HoldingReport, 'isCashEquivalent'>> & {
    financialProduct: Pick<FinancialProduct, 'id' | 'isCash'>;
  };

export type CustomAllocationSubAccountReport = Pick<
  SubAccountReport,
  'id' | 'isTradeable' | 'marketValueCents'
> & {
  subAccount: Pick<SubAccount, 'id' | 'state'>;
  holdings: CustomAllocationHoldingReport[];
};

export type CustomAllocationReport = Pick<
  PortfolioReport,
  'id' | 'expectedCashPercentage'
> &
  Partial<Pick<PortfolioReport, 'modelPortfolio'>> & {
    subAccounts: CustomAllocationSubAccountReport[];
    holdings: CustomAllocationHoldingReport[];
  };

export type AllocationDraftCell = {
  subAccountId: string;
  financialProductId: string;
  customExpectedPercentage: number; // fraction 0..1
};

export type HoldingCustomExpectedPercentageInput = {
  financialProductId: string;
  customExpectedPercentage: number;
};

export type SubAccountCustomAllocationInput = {
  subAccountId: string;
  overrides: HoldingCustomExpectedPercentageInput[];
};

// Asset-class models store `customExpectedPercentage` as a WITHIN-GROUP fraction
// (backend weight = custom × assetClassGroupPercentage). The editor works in
// PORTFOLIO-LEVEL fractions, so values convert only at the wire boundary
// (save/reset) and when read back from the report.
export const isAssetClassAllocationModel = (
  portfolioReport: CustomAllocationReport,
) =>
  portfolioReport.modelPortfolio?.strategy ===
  FinancialProductStrategy.AssetClass;

// Mirrors the backend: a missing group percentage counts as 1. Standard models never convert.
const wireGroupPercentage = (
  isAssetClass: boolean,
  holding: Pick<HoldingReport, 'assetClassGroupPercentage'>,
) => (isAssetClass ? (holding.assetClassGroupPercentage ?? 1) : 1);

// Clamped to 1: a cell rounded to whole cents at its cap lands a hair above 1 after the division,
// and the backend rejects within-group values over 1. Genuine overshoots never reach here (Save is blocked).
export const toWireFraction = (
  portfolioFraction: number,
  groupPercentage: number,
) =>
  groupPercentage > 0
    ? Math.min(roundToWireDecimals(portfolioFraction / groupPercentage), 1)
    : 0;

export const storedPortfolioFraction = (
  holding: Pick<
    HoldingReport,
    'customExpectedPercentage' | 'assetClassGroupPercentage'
  >,
  isAssetClass: boolean,
) => {
  const custom = holding.customExpectedPercentage;
  if (custom === undefined || custom === null) return undefined;
  return custom * wireGroupPercentage(isAssetClass, holding);
};

const wireGroupByKey = (portfolioReport: CustomAllocationReport) => {
  const isAssetClass = isAssetClassAllocationModel(portfolioReport);
  const groups = new Map<string, number>();

  for (const subAccountReport of tradeableAllocationSubAccounts(
    portfolioReport.subAccounts,
  )) {
    const subAccountId = subAccountReportId(subAccountReport);
    for (const holding of subAccountReport.holdings ?? []) {
      const financialProductId = holding.financialProduct?.id;
      if (!financialProductId) continue;
      groups.set(
        allocationCellKey(subAccountId, financialProductId),
        wireGroupPercentage(isAssetClass, holding),
      );
    }
  }

  return groups;
};

export const toWireSubAccounts = (
  portfolioReport: CustomAllocationReport,
  subAccounts: SubAccountCustomAllocationInput[],
) => {
  const groupByKey = wireGroupByKey(portfolioReport);

  return subAccounts.map(({ subAccountId, overrides }) => ({
    subAccountId,
    overrides: overrides.map(
      ({ financialProductId, customExpectedPercentage }) => ({
        financialProductId,
        customExpectedPercentage: toWireFraction(
          customExpectedPercentage,
          groupByKey.get(allocationCellKey(subAccountId, financialProductId)) ??
            1,
        ),
      }),
    ),
  }));
};

// The backend caps each within-group value at 1, so the cap in portfolio units is the
// security's group weight. Standard models have no per-security cap (empty map).
export const allocationCapacityByKey = (
  portfolioReport: CustomAllocationReport,
) =>
  isAssetClassAllocationModel(portfolioReport)
    ? wireGroupByKey(portfolioReport)
    : new Map<string, number>();

export type BuildChangedCustomAllocationCellsOptions = {
  /** Staged dollar locks — exact whole cents take precedence over draft fractions. */
  amountCentsByKey?: ReadonlyMap<string, number>;
  mvCentsBySubAccountId?: ReadonlyMap<string, number>;
  /** 1 − expectedCashPercentage; required for BE-valid wire save fractions. */
  securityMassFraction?: number;
};

/** Groups changed cells into the setCustomExpectedPercentages `subAccounts` payload (insertion order preserved). */
export const buildCustomAllocationSubAccounts = (
  cells: AllocationDraftCell[],
): SubAccountCustomAllocationInput[] => {
  const bySubAccount = new Map<string, Map<string, number>>();
  for (const cell of cells) {
    const overrides =
      bySubAccount.get(cell.subAccountId) ?? new Map<string, number>();
    overrides.set(cell.financialProductId, cell.customExpectedPercentage);
    bySubAccount.set(cell.subAccountId, overrides);
  }

  return [...bySubAccount.entries()].map(([subAccountId, overrides]) => ({
    subAccountId,
    overrides: [...overrides.entries()].map(
      ([financialProductId, customExpectedPercentage]) => ({
        financialProductId,
        customExpectedPercentage,
      }),
    ),
  }));
};

const modelTargetByFinancialProductId = (
  portfolioReport: CustomAllocationReport,
): Map<string, number> => {
  const modelByFp = new Map<string, number>();

  for (const holding of portfolioReport.holdings ?? []) {
    const financialProductId = holding.financialProduct?.id;
    if (!financialProductId) continue;
    modelByFp.set(financialProductId, holding.expectedPercentage);
  }

  return modelByFp;
};

/** True when any tradeable member holding has a persisted override that differs from the household model target. */
export const hasCustomAllocationOverrides = (
  portfolioReport: CustomAllocationReport,
): boolean => {
  const modelByFp = modelTargetByFinancialProductId(portfolioReport);
  const isAssetClass = isAssetClassAllocationModel(portfolioReport);

  for (const subAccountReport of tradeableAllocationSubAccounts(
    portfolioReport.subAccounts,
  )) {
    for (const holding of subAccountReport.holdings ?? []) {
      const custom = storedPortfolioFraction(holding, isAssetClass);
      if (custom === undefined) continue;

      const financialProductId = holding.financialProduct?.id;
      if (!financialProductId) continue;

      const modelTarget = modelByFp.get(financialProductId);
      if (modelTarget === undefined) return true;
      if (Math.abs(custom - modelTarget) > MODEL_SNAP_TOLERANCE) {
        return true;
      }
    }
  }

  return false;
};

const buildCanonicalWireFractionsByKey = (
  draft: Map<string, number>,
  modelByFp: Map<string, number>,
  options?: BuildChangedCustomAllocationCellsOptions,
): Map<string, number> => {
  const canonicalByKey = new Map<string, number>();
  const securityMassFraction = options?.securityMassFraction;
  if (securityMassFraction === undefined || !options?.mvCentsBySubAccountId) {
    return canonicalByKey;
  }

  const matrixCells: {
    key: string;
    subAccountId: string;
    financialProductId: string;
    amountCents: number;
    mvCents: number;
  }[] = [];

  for (const [key, fraction] of draft) {
    const { subAccountId, financialProductId } = parseAllocationCellKey(key);
    const mvCents = options.mvCentsBySubAccountId.get(subAccountId);
    if (!mvCents) continue;

    matrixCells.push({
      key,
      subAccountId,
      financialProductId,
      amountCents:
        options.amountCentsByKey?.get(key) ??
        fractionToAmountCents(fraction, mvCents),
      mvCents,
    });
  }

  if (matrixCells.length === 0) return canonicalByKey;

  const mvTotalCents = [...options.mvCentsBySubAccountId.values()].reduce(
    (sum, mvCents) => sum + mvCents,
    0,
  );
  const rowTargetCents = buildHouseholdRowTargetCents(
    modelByFp,
    mvTotalCents,
    securityMassFraction,
  );
  const lockedCentsBySubAccountId = new Map<string, number>();
  for (const [key, amountCents] of options.amountCentsByKey ?? []) {
    const { subAccountId } = parseAllocationCellKey(key);
    lockedCentsBySubAccountId.set(
      subAccountId,
      (lockedCentsBySubAccountId.get(subAccountId) ?? 0) + amountCents,
    );
  }
  const members = [...options.mvCentsBySubAccountId.entries()].map(
    ([sa, mvCents]) => ({ sa, mvCents }),
  );
  const colTargetCents = buildMemberColumnTargetCents(
    members,
    securityMassFraction,
    Math.round(mvTotalCents * securityMassFraction),
    lockedCentsBySubAccountId,
  );

  return canonicalizeWireMatrixFractions(
    matrixCells,
    rowTargetCents,
    colTargetCents,
  );
};

// A $0 account is compared as fractions: through cents every value reads 0.
const fundedMvCents = (
  options: BuildChangedCustomAllocationCellsOptions | undefined,
  subAccountId: string,
) => {
  const mvCents = options?.mvCentsBySubAccountId?.get(subAccountId);
  return mvCents && mvCents > 0 ? mvCents : undefined;
};

const buildChangedPortfolioFractionCells = (
  draft: Map<string, number>,
  portfolioReport: CustomAllocationReport,
  options?: BuildChangedCustomAllocationCellsOptions,
): AllocationDraftCell[] => {
  const modelByFp = modelTargetByFinancialProductId(portfolioReport);
  const isAssetClass = isAssetClassAllocationModel(portfolioReport);
  const persistedByKey = new Map<string, number>();
  const canonicalWireByKey = buildCanonicalWireFractionsByKey(
    draft,
    modelByFp,
    options,
  );

  for (const subAccountReport of tradeableAllocationSubAccounts(
    portfolioReport.subAccounts,
  )) {
    const subAccountId = subAccountReportId(subAccountReport);

    for (const holding of subAccountReport.holdings ?? []) {
      const financialProductId = holding.financialProduct?.id;
      if (!financialProductId) continue;

      const modelTarget = modelByFp.get(financialProductId);
      if (modelTarget === undefined) continue;

      const persisted =
        storedPortfolioFraction(holding, isAssetClass) ?? modelTarget;
      persistedByKey.set(
        allocationCellKey(subAccountId, financialProductId),
        persisted,
      );
    }
  }

  const cells: AllocationDraftCell[] = [];
  const changedSubAccounts = new Set<string>();
  for (const [key, fraction] of draft) {
    const persisted = persistedByKey.get(key);
    if (persisted === undefined) continue;

    const { subAccountId, financialProductId } = parseAllocationCellKey(key);
    const mvCents = fundedMvCents(options, subAccountId);
    const amountCents =
      options?.amountCentsByKey?.get(key) ??
      (mvCents !== undefined
        ? fractionToAmountCents(fraction, mvCents)
        : undefined);
    const wireFraction =
      canonicalWireByKey.get(key) ??
      (mvCents !== undefined && amountCents !== undefined
        ? fractionFromAmountCents(amountCents, mvCents)
        : fraction);

    const persistedCents =
      mvCents !== undefined
        ? fractionToAmountCents(persisted, mvCents)
        : undefined;
    const resolvedAmountCents =
      amountCents ??
      (mvCents !== undefined
        ? fractionToAmountCents(fraction, mvCents)
        : undefined);

    const unchanged =
      resolvedAmountCents !== undefined && persistedCents !== undefined
        ? resolvedAmountCents === persistedCents
        : Math.abs(wireFraction - persisted) <= CUSTOM_OVERRIDE_TOLERANCE;

    if (unchanged) {
      continue;
    }

    cells.push({
      subAccountId,
      financialProductId,
      customExpectedPercentage: wireFraction,
    });
    changedSubAccounts.add(subAccountId);
  }

  if (canonicalWireByKey.size === 0 || changedSubAccounts.size === 0) {
    return cells;
  }

  const cellsByKey = new Map(
    cells.map((cell) => [
      allocationCellKey(cell.subAccountId, cell.financialProductId),
      cell,
    ]),
  );

  for (const [key, wireFraction] of canonicalWireByKey) {
    const { subAccountId, financialProductId } = parseAllocationCellKey(key);
    if (!changedSubAccounts.has(subAccountId)) continue;
    if (!persistedByKey.has(key)) continue;

    cellsByKey.set(key, {
      subAccountId,
      financialProductId,
      customExpectedPercentage: wireFraction,
    });
  }

  return [...cellsByKey.values()];
};

/** Cells whose draft fraction differs from persisted override or model target (BE save diff). */
// Returned in wire units: asset-class values are divided by their group percentage.
export const buildChangedCustomAllocationCells = (
  draft: Map<string, number>,
  portfolioReport: CustomAllocationReport,
  options?: BuildChangedCustomAllocationCellsOptions,
): AllocationDraftCell[] => {
  const groupByKey = wireGroupByKey(portfolioReport);

  return buildChangedPortfolioFractionCells(
    draft,
    portfolioReport,
    options,
  ).map((cell) => ({
    ...cell,
    customExpectedPercentage: toWireFraction(
      cell.customExpectedPercentage,
      groupByKey.get(
        allocationCellKey(cell.subAccountId, cell.financialProductId),
      ) ?? 1,
    ),
  }));
};

/** Save payload that resets every held security back to the household model target per tradeable member. */
export const buildResetCustomAllocationSubAccounts = (
  portfolioReport: CustomAllocationReport,
): SubAccountCustomAllocationInput[] => {
  const modelByFp = modelTargetByFinancialProductId(portfolioReport);
  const isAssetClass = isAssetClassAllocationModel(portfolioReport);
  const cells: AllocationDraftCell[] = [];

  for (const subAccountReport of tradeableAllocationSubAccounts(
    portfolioReport.subAccounts,
  )) {
    const subAccountId = subAccountReportId(subAccountReport);

    for (const holding of subAccountReport.holdings ?? []) {
      const financialProductId = holding.financialProduct?.id;
      if (!financialProductId) continue;

      const modelTarget = modelByFp.get(financialProductId);
      if (modelTarget === undefined) continue;

      cells.push({
        subAccountId,
        financialProductId,
        customExpectedPercentage: toWireFraction(
          modelTarget,
          wireGroupPercentage(isAssetClass, holding),
        ),
      });
    }
  }

  return buildCustomAllocationSubAccounts(cells);
};
